import type { ReactNode } from 'react';

type ContentRow = { key: string; bold?: boolean };
type AccordionSection = {
  titleKey: string;
  content: ContentRow[][];
};

const SECTIONS: AccordionSection[] = [
  {
    titleKey: 'photon.photonTechSpecs.label.model',
    content: [[{ key: 'photon.photonTechSpecs.value.model' }]],
  },
  {
    titleKey: 'photon.photonTechSpecs.label.body',
    content: [
      [
        { key: 'photon.photonTechSpecs.label.dimensions', bold: true },
        { key: 'photon.photonTechSpecs.value.dimensions' },
      ],
      [
        { key: 'photon.photonTechSpecs.label.weight', bold: true },
        { key: 'photon.photonTechSpecs.value.weight' },
      ],
      [
        { key: 'photon.photonTechSpecs.label.finish', bold: true },
        { key: 'photon.photonTechSpecs.value.finish' },
      ],
    ],
  },
  {
    titleKey: 'photon.photonTechSpecs.label.optical',
    content: [
      [
        { key: 'photon.photonTechSpecs.label.wavelengths', bold: true },
        { key: 'photon.photonTechSpecs.value.wavelengths' },
      ],
      [
        { key: 'photon.photonTechSpecs.label.leds', bold: true },
        { key: 'photon.photonTechSpecs.value.leds' },
      ],
      [
        { key: 'photon.photonTechSpecs.label.irradiance', bold: true },
        { key: 'photon.photonTechSpecs.value.irradiance' },
      ],
      [
        { key: 'photon.photonTechSpecs.label.powerOutput', bold: true },
        { key: 'photon.photonTechSpecs.value.powerOutput' },
      ],
    ],
  },
  {
    titleKey: 'photon.photonTechSpecs.label.battery',
    content: [
      [
        { key: 'photon.photonTechSpecs.label.capacity', bold: true },
        { key: 'photon.photonTechSpecs.value.capacity' },
      ],
      [
        { key: 'photon.photonTechSpecs.label.sessionsPerCharge', bold: true },
        { key: 'photon.photonTechSpecs.value.sessionsPerCharge' },
      ],
      [
        { key: 'photon.photonTechSpecs.label.charging', bold: true },
        { key: 'photon.photonTechSpecs.value.charging' },
      ],
    ],
  },
  {
    titleKey: 'photon.photonTechSpecs.label.powerAdapter',
    content: [
      [
        { key: 'photon.photonTechSpecs.label.inputRange', bold: true },
        { key: 'photon.photonTechSpecs.value.inputRange' },
      ],
      [
        { key: 'photon.photonTechSpecs.label.output', bold: true },
        { key: 'photon.photonTechSpecs.value.output' },
      ],
      [
        { key: 'photon.photonTechSpecs.label.type', bold: true },
        { key: 'photon.photonTechSpecs.value.type' },
      ],
    ],
  },
];

type TFunction = (key: string) => string;

const resolveSections = (
  sections: AccordionSection[],
  t: TFunction,
): Array<{ title: ReactNode; content: ReactNode }> =>
  sections.map((section) => ({
    title: t(section.titleKey),
    content: (
      <>
        {section.content.map((block, blockIdx) => (
          <div key={blockIdx} className="content">
            {block.map((row, rowIdx) => (
              <div key={rowIdx} className="content-row">
                {row.bold ? (
                  <span className="bold">{t(row.key)}</span>
                ) : (
                  <span>{t(row.key)}</span>
                )}
              </div>
            ))}
          </div>
        ))}
      </>
    ),
  }));

export const getPhotonTechSpecsImages = (t: TFunction) => [
  {
    src: 'web_v2/photon/tech-specs/spec-white.png',
    alt: t('photon.photonTechSpecs.image.ledPanel'),
    textBold: t('photon.photonTechSpecs.text.twelveLeds'),
    textNormal: t('photon.photonTechSpecs.text.singlePanel'),
  },
  {
    src: 'web_v2/photon/tech-specs/spec-black.png',
    alt: t('photon.photonTechSpecs.image.dualWavelengthLeds'),
    textBold: t('photon.photonTechSpecs.text.dualWavelength'),
    textNormal: t('photon.photonTechSpecs.text.bothWavelengths'),
  },
];

export const getPhotonTechSpecsAccordionItems = (t: TFunction) =>
  resolveSections(SECTIONS, t);
